import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get, put } from 'aws-amplify/api';
import { signOut } from 'aws-amplify/auth';
import LoadingSpinner from '../components/LoadingSpinner';
import DomainManagement from '../components/DomainManagement';
import { currentAuthenticatedUser, currentSession } from '../global';
import '../styles/management.css';


import { setDocumentTitle } from '../utils/titleManager';

function ProfileScreen() {
  useEffect(() => {
    setDocumentTitle('Profile');
  }, []);

  // Check authentication on component load
  useEffect(() => {
    async function checkAuth() {
      try {
        const user = await currentAuthenticatedUser();
        const session = await currentSession();
        console.log('Authenticated user:', user);
        console.log('Current session:', session);
      } catch (error) {
        console.error('Authentication check failed:', error);
      }
    }
    checkAuth();
  }, []);
  
  const { eventId } = useParams();
  // Navigation handled by event handlers directly
    
  const [userProfile, setUserProfile] = useState(null);
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAttributes, setEditedAttributes] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo();
    fetchDomains();
  }, [eventId]);

  async function fetchUserInfo() {
    setIsLoading(true);
    setError(null);

    try {
      const restOperation = get({
        apiName: 'events',
        path: `/profile`,
        withCredentials: true
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();

      console.log('Got /profile response:', json);
      console.info('Debug fetched user info:', body);

      setUserProfile(json);
      setEditedAttributes(json.attributes);
    } catch (error) {
      console.error('Error fetching user info:', error);
      setError('Failed to load user profile');
    } finally {
      setIsLoading(false);
    }
  }
  
  async function fetchDomains() {
    try {
      const restOperation = get({ 
        apiName: 'events',
        path: `/profile/domains`,
        withCredentials: true
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();
      console.info('Debug fetched domains:', json);
      setDomains(json || []);
    } catch (error) {
      console.error('Error fetching domains:', error);
      // Don't set error to avoid disrupting the profile page if domain fetch fails
    }
  }

  async function handleUpdateProfile(e) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await put({
        apiName: 'events',
        path: '/profile',
        withCredentials: true,
        options: {
          body: {
            attributes: editedAttributes
          }
        }
      });
      setIsEditing(false);
      await fetchUserInfo();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  }

  const handleInputChange = (name, value) => {
    setEditedAttributes(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!userProfile || isLoading) {
    return (
      <>
        <LoadingSpinner />
      </>
    );
  }

  return (
    <div>
      <div className="container mx-auto p-4">
        <h2 className="text-2xl font-bold mb-4">User Profile</h2>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {userProfile.welcome && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            <h3 className="font-bold text-lg mb-2">👋 Welcome to Event Planner!</h3>
            <p>
              We&apos;re excited to have you here! To get started, please set up your profile information 
              by clicking the &quot;Edit Profile&quot; button below. This will help personalize your experience.
            </p>
          </div>
        )}
        
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <strong>Username:</strong> {userProfile.username}
          </div>
          
          {!isEditing ? (
            <>
              <div className="space-y-2">
                {userProfile.attributes ? (
                <>
                <p><strong>Email:</strong> {userProfile.attributes?.email || '!! Not set !!'}</p>
                <p><strong>Name:</strong> {userProfile.attributes?.name || 'Not set'}</p>
                <p><strong>Phone Number:</strong> {userProfile.attributes?.phone_number || 'Not set'}</p>
                <p><strong>Address:</strong> {userProfile.attributes?.address || 'Not set'}</p>
                <p><strong>Birthdate:</strong> {userProfile.attributes?.birthdate || 'Not set'}</p>
                </>
                ) : (
                  <>
                  Unable to load attributes
                  </>
                )}
              </div>
              <div className="mt-4 space-x-2">
                <button
                  className="button button--secondary"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </button>
                <button
                  className="button button--danger"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </div>
              
              {/* Custom Domains Section */}
              <div className="mt-8 border-t pt-6">
                <DomainManagement 
                  domains={domains} 
                  onDomainAdded={fetchDomains} 
                />
              </div>
            </>
          ) : (
            <form onSubmit={handleUpdateProfile}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={editedAttributes?.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={editedAttributes?.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    value={editedAttributes?.phone_number || ''}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    value={editedAttributes?.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Birthdate</label>
                  <input
                    type="date"
                    value={editedAttributes?.birthdate || ''}
                    onChange={(e) => handleInputChange('birthdate', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              </div>
              <div className="mt-4 space-x-2">
                <button
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedAttributes(userProfile.attributes);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileScreen;

